<template>
  <div
    class="sap"
    :class="['sap--' + variant, { 'sap--bare': !label, 'sap--multiple': multiple }]"
    :style="{ '--sap-h': height + 'px' }"
  >
    <v-autocomplete
      ref="autocomplete"
      v-model="model"
      :items="items"
      :loading="isLoading"
      :search-input.sync="search"
      :label="label"
      :placeholder="placeholder"
      :rules="rules || []"
      :hide-details="rules ? 'auto' : true"
      :multiple="!!multiple"
      :chips="!!multiple"
      :small-chips="!!multiple"
      :disabled="disabled"
      :readonly="isMobile"
      :menu-props="{ maxHeight: 280, offsetY: true, contentClass: 'sap-menu' }"
      item-text="name"
      item-value="id"
      append-icon=""
      return-object
      no-filter
      clearable
      :outlined="variant === 'outlined'"
      :solo="variant !== 'outlined'"
      :flat="variant !== 'outlined'"
      dense
      @focus="onFocus"
      @click="openMobile"
      @blur="$emit('blur')"
      @change="$emit('change', $event)"
      @click:clear="onClear"
    >
      <template v-slot:append>
        <div class="sap__append">
          <v-tooltip v-if="append" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" icon x-small color="primary" tabindex="-1" v-on="on" @click.stop.prevent="setDefault">
                <v-icon small>mdi-account-check-outline</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('set-to-default') }}</span>
          </v-tooltip>
          <v-tooltip v-if="createAction" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" icon x-small color="primary" tabindex="-1" v-on="on" @click.stop.prevent="openCreate(createAction.dialog)">
                <v-icon small>{{ createAction.icon }}</v-icon>
              </v-btn>
            </template>
            <span>{{ $t(createAction.label) }}</span>
          </v-tooltip>
          <v-icon small class="sap__chevron">mdi-chevron-down</v-icon>
        </div>
      </template>

      <template v-if="type === 'detail' && newBtn" v-slot:prepend-item>
        <div class="sap__quick">
          <v-btn small text color="primary" @click.stop.prevent="openCreate('detail')">
            <v-icon small left>mdi-playlist-plus</v-icon>{{ $t('newdetail') }}
          </v-btn>
          <v-btn small text color="primary" @click.stop.prevent="openCreate('account')">
            <v-icon small left>mdi-account-plus-outline</v-icon>{{ $t('new-account') }}
          </v-btn>
          <v-btn small text color="primary" @click.stop.prevent="openCreate('product')">
            <v-icon small left>mdi-package-variant</v-icon>{{ $t('new-product') }}
          </v-btn>
        </div>
        <v-divider />
      </template>

      <template v-if="!multiple" v-slot:selection="{ item }">
        <span class="sap__selection">{{ item.name }}</span>
      </template>
      <template v-else v-slot:selection="{ item, index }">
        <v-chip v-if="index < 2" small close :color="item.color || 'primary'" dark class="my-1" @click:close="remove(item)">
          {{ item.name }}
        </v-chip>
        <span v-else-if="index === 2" class="sap__more">+{{ model.length - 2 }} {{ $t('otherItems') }}</span>
      </template>

      <template v-slot:item="{ item }">
        <v-list-item-content>
          <v-list-item-title class="text-wrap">{{ item.name }}</v-list-item-title>
          <v-list-item-subtitle v-if="item.code" class="sap__code">{{ item.code }}</v-list-item-subtitle>
        </v-list-item-content>
        <v-list-item-action v-if="item.label && type !== 'detail'" class="my-0">
          <v-chip x-small label outlined :color="item.color || 'primary'">{{ $t(item.label) }}</v-chip>
        </v-list-item-action>
      </template>

      <template v-slot:no-data>
        <div class="sap__empty">{{ isLoading ? $t('searching') : $t('no-data') }}</div>
      </template>

      <template v-slot:append-item>
        <div v-if="hasMore" v-intersect="onIntersect" class="sap__more-loader">
          <v-progress-circular :size="18" :width="2" color="primary" indeterminate />
        </div>
      </template>
    </v-autocomplete>

    <!-- Mobile: full-screen picker -->
    <v-dialog
      v-if="isMobile"
      v-model="mobileDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <div class="sap-m">
        <header class="sap-m__bar">
          <!-- native button: a global override paints icon v-btns white -->
          <button type="button" class="sap-m__back" :aria-label="$t('back')" @click="mobileDialog = false">
            <v-icon>mdi-arrow-right</v-icon>
          </button>
          <div class="sap-m__title">{{ label || placeholder }}</div>
          <v-btn v-if="createAction" icon color="primary" @click="openCreate(createAction.dialog)">
            <v-icon>{{ createAction.icon }}</v-icon>
          </v-btn>
        </header>
        <div class="sap-m__search">
          <v-icon color="grey">mdi-magnify</v-icon>
          <input
            v-model="mobileSearch"
            type="search"
            class="sap-m__input"
            :placeholder="$t('search')"
            autocomplete="off"
            @keydown.enter.prevent
          >
          <v-btn v-if="mobileSearch" icon x-small @click="mobileSearch = ''"><v-icon small>mdi-close-circle</v-icon></v-btn>
        </div>
        <v-progress-linear v-if="isLoading" indeterminate color="primary" height="2" />
        <div class="sap-m__body">
          <button
            v-for="(item, i) in items"
            :key="item.id != null ? item.id : 'i' + i"
            type="button"
            class="sap-m__item"
            :class="{ 'sap-m__item--on': isSelected(item) }"
            @click="pickMobile(item)"
          >
            <span class="sap-m__index">{{ i + 1 }}</span>
            <span class="sap-m__text">
              <span class="sap-m__name">{{ item.name }}</span>
              <span v-if="item.code" class="sap-m__code">{{ item.code }}</span>
            </span>
            <v-icon v-if="isSelected(item)" color="primary" small>mdi-check-circle</v-icon>
          </button>
          <div v-if="hasMore" v-intersect="onIntersect" class="sap__more-loader">
            <v-progress-circular :size="20" :width="2" color="primary" indeterminate />
          </div>
          <div v-if="!isLoading && !items.length" class="sap__empty">{{ $t('no-data') }}</div>
        </div>
        <footer v-if="multiple" class="sap-m__foot">
          <v-btn color="primary" depressed block large @click="mobileDialog = false">{{ $t('submit') }}</v-btn>
        </footer>
      </div>
    </v-dialog>

    <!-- Quick-create dialogs (components are loaded on first open) -->
    <v-dialog v-model="createDialog.open" :fullscreen="isMobile" :hide-overlay="isMobile" width="95%" scrollable>
      <v-card v-if="createDialog.open">
        <v-card-title class="sap-dialog__bar">
          <v-btn icon @click="createDialog.open = false"><v-icon>mdi-close</v-icon></v-btn>
          <span class="sap-dialog__title">{{ $t(createDialogTitle) }}</span>
        </v-card-title>
        <v-card-text class="pa-2">
          <customer v-if="createDialog.kind === 'account'" :return-object="true" @input="onCreated" @close="createDialog.open = false" />
          <accounting-coding v-else-if="createDialog.kind === 'given'" :return-object="true" @input="onCreated" @close="createDialog.open = false" />
          <detail v-else-if="createDialog.kind === 'detail'" :return-object="true" @input="onCreated" @close="createDialog.open = false" />
          <product v-else-if="createDialog.kind === 'product'" form-type="product" @input="onCreated" @close="createDialog.open = false" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Settings-only copy of components/general/patial/autocomplete.vue.
// Same API contract (getdetail?select=<type>, v-model = selected object(s)),
// trimmed to what the settings pages use and kept separate so changes here
// never affect the rest of the app.
const PAGE_DEBOUNCE = 350

const CREATE_ACTIONS = {
  given: { dialog: 'given', icon: 'mdi-plus', label: 'given' },
  person: { dialog: 'account', icon: 'mdi-account-plus-outline', label: 'new-account', permission: 'customer' },
  product: { dialog: 'product', icon: 'mdi-plus', label: 'product', permission: 'Product' },
  productService: { dialog: 'product', icon: 'mdi-plus', label: 'product', permission: 'Product' },
}

const DIALOG_TITLES = {
  account: 'new-account',
  given: 'accountStracture',
  detail: 'accountStracture',
  product: 'new-product',
}

function idOf (item) {
  return item && typeof item === 'object' ? item.id : item
}

export default {
  name: 'SettingAccountPicker',
  components: {
    customer: () => import('../../account/customer'),
    AccountingCoding: () => import('../../accounting/coding'),
    Detail: () => import('../../account/detail'),
    Product: () => import('../../product/product'),
  },
  props: {
    value: { type: [Object, Array, Number, String], default: null },
    type: { type: String, required: true },
    title: { type: String, default: null },
    // outlined (default) | soft: tinted, borderless — use inside cards/rows
    // so there is no outline-inside-outline | plain: transparent, inline
    variant: {
      type: String,
      default: 'outlined',
      validator: v => ['outlined', 'soft', 'plain'].includes(v),
    },
    placeholder: { type: String, default: null },
    newBtn: { type: Boolean, default: false },
    multiple: { type: Boolean, default: false },
    rules: { type: Array, default: null },
    disabled: { type: Boolean, default: false },
    append: { type: Boolean, default: false },
    setInit: { type: Boolean, default: false },
    data: { type: [String, Object, Number], default: undefined },
    parent: { type: [String, Number], default: undefined },
    typeProp: { type: String, default: undefined },
    currencyProp: { type: [String, Number], default: null },
    // Field height in px; match it to neighbouring inputs (dense text = 40).
    height: { type: [Number, String], default: 40 },
  },
  data: () => ({
    model: null,
    items: [],
    search: null,
    mobileSearch: '',
    mobileDialog: false,
    isLoading: false,
    page: 1,
    lastPage: 1,
    loadedOnce: false,
    createDialog: { open: false, kind: null },
  }),
  computed: {
    label () {
      return this.title ? this.$t(this.title) : null
    },
    query () {
      return ((this.isMobile ? this.mobileSearch : this.search) || '').trim()
    },
    hasMore () {
      return this.items.length > 0 && this.page <= this.lastPage && this.lastPage > 1
    },
    createAction () {
      if (!this.newBtn) return null
      const action = CREATE_ACTIONS[this.type]
      if (!action) return null
      if (action.permission) {
        const perms = this.$store.state.permissions
        if (!perms || !perms.includes(action.permission)) return null
      }
      return action
    },
    createDialogTitle () {
      return DIALOG_TITLES[this.createDialog.kind] || ''
    },
    currency () {
      if (this.currencyProp) return this.currencyProp
      const c = this.$store.state.currency
      return c ? c.id : null
    },
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (this.sameAsModel(val)) return
        if (val) {
          this.mergeItems(this.multiple ? Array.from(val) : [val])
          this.model = val
        } else {
          this.model = this.multiple ? [] : null
        }
      },
    },
    model (val) {
      if (!this.sameAsModel(this.value)) this.$emit('input', val == null ? null : val)
    },
    search (val) {
      // Selecting an item writes its name into the search box; skip that.
      if (!this.multiple && this.model && this.model.name === val) return
      this.resetAndFetch()
    },
    mobileSearch () {
      this.resetAndFetch()
    },
    currencyProp () {
      this.resetAndFetch(true)
    },
    // Mobile picker: the phone's back button closes the list instead of
    // navigating away (a history entry is pushed while it is open).
    mobileDialog (open) {
      if (open) this.claimBack()
      else this.releaseBack(true)
    },
  },
  mounted () {
    if (this.setInit && !this.value) this.fetch()
  },
  beforeDestroy () {
    clearTimeout(this.debounce)
    if (this.cancelSource) this.cancelSource.cancel()
    this.releaseBack(false)
  },
  methods: {
    sameAsModel (val) {
      if (this.multiple) {
        const a = (val || []).map(idOf).join(',')
        const b = (this.model || []).map(idOf).join(',')
        return a === b
      }
      return idOf(val) === idOf(this.model) && (val == null) === (this.model == null)
    },
    mergeItems (list) {
      const seen = new Set(this.items.map(idOf))
      const fresh = list.filter(i => i && !seen.has(idOf(i)))
      if (fresh.length) this.items = [...this.items, ...fresh]
    },
    selectedList () {
      if (!this.model) return []
      return this.multiple ? this.model : [this.model]
    },
    onFocus () {
      if (!this.loadedOnce) this.fetch()
    },
    onClear () {
      this.$emit('clear')
      this.resetAndFetch()
    },
    onIntersect (entries) {
      if (entries[0] && entries[0].isIntersecting && !this.isLoading) this.fetch()
    },
    resetAndFetch (immediate) {
      clearTimeout(this.debounce)
      const run = () => {
        this.page = 1
        this.lastPage = 1
        this.items = this.selectedList().slice()
        this.fetch()
      }
      if (immediate) run()
      else this.debounce = setTimeout(run, PAGE_DEBOUNCE)
    },
    fetch () {
      if (this.cancelSource) this.cancelSource.cancel('superseded')
      this.cancelSource = this.$axios.CancelToken.source()
      this.isLoading = true
      this.loadedOnce = true
      const page = this.page
      this.$axios.get(this.$endpoint + 'getdetail', {
        params: {
          select: this.type,
          search: this.query,
          page,
          currency: this.currency,
          data: this.data,
          parent_id: this.parent,
          formType: this.typeProp,
        },
        cancelToken: this.cancelSource.token,
      })
        .then(res => {
          const body = res.data || {}
          const rows = Array.isArray(body) ? body : (body.data || [])
          this.lastPage = body.last_page || 1
          this.page = page + 1
          this.mergeItems(rows)
          if (this.setInit && page === 1 && !this.model && rows.length) {
            this.model = this.multiple ? [rows[0]] : rows[0]
          }
        })
        .catch(err => {
          if (!this.$axios.isCancel(err)) this.serverErrorHandeler(err)
        })
        .finally(() => { this.isLoading = false })
    },
    remove (item) {
      this.model = this.model.filter(x => !(x.id === item.id && x.type === item.type))
    },
    isSelected (item) {
      return this.selectedList().some(s => idOf(s) === idOf(item))
    },
    claimBack () {
      if (this.backClaimed) return
      this.backClaimed = true
      window.history.pushState({ ...(window.history.state || {}), sapPicker: true }, '')
      this.onPopState = () => {
        // Back pressed: our entry is already gone, just close.
        this.backClaimed = false
        window.removeEventListener('popstate', this.onPopState)
        this.mobileDialog = false
      }
      window.addEventListener('popstate', this.onPopState)
    },
    releaseBack (popEntry) {
      if (!this.backClaimed) return
      this.backClaimed = false
      window.removeEventListener('popstate', this.onPopState)
      // Closed from the UI: drop the entry we pushed so history stays clean.
      if (popEntry && window.history.state && window.history.state.sapPicker) {
        window.history.back()
      }
    },
    openMobile () {
      if (!this.isMobile || this.disabled) return
      this.mobileDialog = true
      if (!this.loadedOnce) this.fetch()
    },
    pickMobile (item) {
      if (this.multiple) {
        this.model = this.isSelected(item)
          ? this.model.filter(s => idOf(s) !== idOf(item))
          : [...(this.model || []), item]
        return
      }
      this.model = item
      this.mobileDialog = false
    },
    openCreate (kind) {
      this.createDialog = { open: true, kind }
    },
    onCreated (item) {
      if (!item) return
      if (this.type === 'detail' && item.fin_id) item.id = item.fin_id
      this.createDialog.open = false
      this.mergeItems([item])
      this.model = this.multiple ? [...(this.model || []), item] : item
    },
    setDefault () {
      if (!this.model) return
      this.isLoading = true
      this.$axios.get(this.$endpoint + 'getdetail', {
        params: { select: 'setDefaultAccount', id: this.model },
      })
        .then(res => this.$emit('snackbarShow', { text: res.data.message, color: 'green' }))
        .catch(err => this.serverErrorHandeler(err))
        .finally(() => { this.isLoading = false })
    },
  },
}
</script>

<style scoped>
/* Vuetify adds top margin/padding to text inputs; remove it so the
   picker centers vertically in flex rows. */
.sap ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  /* a global rule pins inputs to 20px; the 40px control then overflows
     downward and the field looks off-centre in flex rows */
  height: auto !important;
}
.sap ::v-deep .v-input__slot {
  min-height: var(--sap-h, 40px) !important;
  margin-bottom: 0 !important;
  align-items: center;
}
.sap ::v-deep .v-text-field__details {
  margin-bottom: 0 !important;
}

/* soft: filled, no border; ring on focus */
.sap--soft ::v-deep .v-input__slot {
  background: rgba(15, 23, 42, 0.045) !important;
  border-radius: 10px !important;
  box-shadow: inset 0 0 0 1px transparent !important;
  transition: box-shadow 0.15s ease, background-color 0.15s ease;
}
.sap--soft ::v-deep .v-input__slot:hover {
  background: rgba(15, 23, 42, 0.07) !important;
}
.sap--soft ::v-deep .v-input--is-focused .v-input__slot {
  background: #fff !important;
  box-shadow: inset 0 0 0 2px var(--v-primary-base, #1976d2) !important;
}
.sap--soft ::v-deep .error--text .v-input__slot {
  box-shadow: inset 0 0 0 1px var(--v-error-base, #ff5252) !important;
}
.theme--dark .sap--soft ::v-deep .v-input__slot {
  background: rgba(255, 255, 255, 0.07) !important;
}

/* plain: transparent, underline on focus */
.sap--plain ::v-deep .v-input__slot {
  background: transparent !important;
  border-radius: 0 !important;
  box-shadow: inset 0 -1px 0 transparent !important;
}
.sap--plain ::v-deep .v-input--is-focused .v-input__slot {
  box-shadow: inset 0 -2px 0 var(--v-primary-base, #1976d2) !important;
}
.sap ::v-deep .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center;
}
/* Single select: keep selection + search input on one line so the field
   stays exactly as tall as a dense text field (40px). */
.sap:not(.sap--multiple) ::v-deep .v-input__slot {
  height: var(--sap-h, 40px) !important;
}
/* The label is positioned relative to .v-select__slot: it must span the
   full 40px (not be a centred 24px box), or the label drops below the
   outline. Centre the text inside it instead. */
.sap:not(.sap--multiple) ::v-deep .v-select__slot {
  align-self: stretch;
  align-items: center;
}
.sap:not(.sap--multiple) ::v-deep .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
  padding: 0 !important;
  min-height: 0 !important;
}
.sap:not(.sap--multiple) ::v-deep .v-select__selections input {
  min-width: 0;
  width: 0;
  flex: 1 1 0;
  padding: 0 !important;
}
.sap ::v-deep .v-select__selection--comma,
.sap__selection {
  margin: 0 !important;
  line-height: 1.4;
}
.sap__append {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}
.sap__chevron {
  opacity: 0.55;
}
.sap__selection {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sap__more {
  font-size: 0.75rem;
  opacity: 0.7;
  margin-inline-start: 4px;
}
.sap__code {
  direction: ltr;
  text-align: end;
  font-size: 0.72rem;
}
.sap__quick {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 8px;
}
.sap__empty {
  padding: 20px 12px;
  text-align: center;
  font-size: 0.82rem;
  opacity: 0.6;
}
.sap__more-loader {
  display: flex;
  justify-content: center;
  padding: 10px 0;
}

.sap-m {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f3f5f9;
}
.theme--dark .sap-m { background: #121212; }
.sap-m__bar {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 60px;
  padding: 8px 12px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.theme--dark .sap-m__bar,
.theme--dark .sap-m__search,
.theme--dark .sap-m__foot { background: #1e1e1e; }
.sap-m__back {
  flex: 0 0 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.05);
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}
.sap-m__back:active {
  background: rgba(0, 0, 0, 0.1);
}
.theme--dark .sap-m__back {
  background: rgba(255, 255, 255, 0.08);
}
.sap-m__title {
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sap-m__search {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  background: #fff;
}
.sap-m__input {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  border: 0;
  outline: none;
  font-size: 15px;
  color: inherit;
}
.sap-m__input::-webkit-search-cancel-button { display: none; }
.sap-m__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sap-m__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 56px;
  padding: 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #fff;
  text-align: start;
  color: inherit;
  -webkit-tap-highlight-color: transparent;
}
.theme--dark .sap-m__item { background: rgba(255, 255, 255, 0.05); }
.sap-m__item--on {
  border-color: var(--v-primary-base, #1976d2);
  background: rgba(25, 118, 210, 0.06);
}
.sap-m__index {
  flex: 0 0 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(0, 0, 0, 0.06);
}
.sap-m__text {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.sap-m__name { font-size: 0.9rem; font-weight: 600; }
.sap-m__code { font-size: 0.72rem; opacity: 0.6; direction: ltr; text-align: end; }
.sap-m__foot {
  flex: 0 0 auto;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: #fff;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.sap-dialog__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px !important;
}
.sap-dialog__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
}
</style>
